import { FINANCE_DIRECTOR_POSITION_ID } from './demoSeed.js';
import { PEOPLE_DOMAIN } from './mockDirectory.js';

/**
 * Environment profiles: what differs between the D365 environments the app runs against live.
 * The build picks one (VITE_ENV_PROFILE); every other setting is the same code. Values are
 * synthetic demo data; the people are fictional.
 *
 * - rsm-sandbox: RSM's own sales-cd9 sandbox (copy-company.rsmd365.com). Build and test here.
 * - legends-trn: the prospect's TRN sandbox (copy-company-sandbox.rsmd365.com, behind Access).
 */

/** A seat in the approval chain above the venue's accountant, created by the app when missing. */
export interface ApproverSeat {
  title: string;
  jobId: string;
  worker: { personnelNumber: string; firstName: string; lastName: string };
}

export interface EnvProfile {
  id: 'rsm-sandbox' | 'legends-trn';
  title: string;
  /** Template company the venues are copied from */
  templateCompany: string;
  /** Pay cycle every new position gets; created first when `create` (TRN has none) */
  payCycle: { id: string; description: string; create: boolean };
  /**
   * Who holds each venue's GL accountant position and its Accountant role: the first person on
   * the roles sheet (null), or this existing D365 user in every company (and their worker).
   */
  accountantUpn: string | null;
  /** Personnel number for the accountant's worker, when the app has to create it */
  accountantPersonnelNumber: string;
  /**
   * The accountant's D365 user ID, for data packages (the live step looks it up), and the name its
   * worker is written with; null when the worker already exists, so a package never touches it.
   */
  accountantUserId: string | null;
  accountantName: string | null;
  /**
   * Where the people package points reporting lines that leave the venue (the sheet's REG-FIN-01,
   * 000613): null where those positions exist as on the sheet (RSM sandbox).
   */
  outsideParentPositionId: string | null;
  /** Workflow owner written into the workflow export for this environment (null: as exported) */
  workflowOwner: string | null;
  /**
   * Where the accountant position reports: an existing position, or a chain the app creates once
   * (first seat at the bottom; each reports to the next) and every venue's accountant joins.
   */
  approvers: { kind: 'existing'; positionId: string } | { kind: 'chain'; seats: readonly ApproverSeat[] };
}

export const ENV_PROFILES: Record<EnvProfile['id'], EnvProfile> = {
  'rsm-sandbox': {
    id: 'rsm-sandbox',
    title: 'RSM sandbox (sales-cd9)',
    templateCompany: 'USMF',
    payCycle: { id: 'M', description: 'Monthly', create: false },
    accountantUpn: null,
    accountantPersonnelNumber: '',
    accountantUserId: null,
    accountantName: null,
    outsideParentPositionId: null,
    workflowOwner: null,
    approvers: { kind: 'existing', positionId: FINANCE_DIRECTOR_POSITION_ID },
  },
  'legends-trn': {
    id: 'legends-trn',
    title: 'Legends TRN sandbox',
    templateCompany: '1100',
    payCycle: { id: 'M', description: 'Monthly', create: true },
    accountantUpn: 'rsmadmin2@LegendsGlobal.com',
    // Rsmadmin2's worker as set up by hand in TRN (2026-10-27: 000332 "RSM Admin 2", linked to the
    // user, primary position 000001). The live step finds it through the user; packages use it as is.
    accountantPersonnelNumber: '000332',
    accountantUserId: 'Rsmadmin2',
    accountantName: null,
    outsideParentPositionId: '000005',
    // The export's owner (SARA) is a USMF demo user that TRN doesn't have.
    workflowOwner: 'Rsmadmin2',
    // The approval chain built by hand in TRN (2026-10-27): GL-Accountant 000001 > Finance-director
    // 000005 (RSM Admin, user rsmadmin) > CFO 000006 (user casey.patel). Each venue's accountant
    // position reports to the Finance director, like 000001; nothing above it is created.
    approvers: { kind: 'existing', positionId: '000005' },
  },
};

export const DEFAULT_ENV_PROFILE = ENV_PROFILES['rsm-sandbox'];

/** The profile a build names (VITE_ENV_PROFILE), or the RSM sandbox. */
export function envProfile(id: string | undefined): EnvProfile {
  return (id && ENV_PROFILES[id as EnvProfile['id']]) || DEFAULT_ENV_PROFILE;
}

/** Contact email for a fictional approver worker. */
export const approverEmail = (s: ApproverSeat): string =>
  `${s.worker.firstName}.${s.worker.lastName}@${PEOPLE_DOMAIN}`.toLowerCase();
